import { useEffect, useRef, useState } from 'react';
import { MapPin, Search } from 'lucide-react';
import { api } from '../../api/client.js';
import { problem } from './util.js';

// Find an address: type a postcode or the start of the address. Addresses
// we've used before come first (free); the rest come from Royal Mail via
// the live address service (Ideal Postcodes, or Google). Each search is one
// session, so a search and its pick are billed together.
export default function AddressFinder({ onPick, linked, autoFocus }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const box = useRef(null);
  const session = useRef(null);
  const newSession = () => { session.current = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9-]/g, ''); };

  useEffect(() => {
    const term = q.trim();
    if (term.length < 3) { setRes(null); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      if (!session.current) newSession();
      api(`/addresses/suggest?q=${encodeURIComponent(term)}&session=${session.current}`)
        .then((r) => { if (live) { setRes(r); setOpen(true); } })
        .catch((e) => { if (live) setMsg({ tone: 'bad', text: problem(e) }); });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]);

  // Close the list when clicking elsewhere.
  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  const pick = async (body) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api('/addresses/pick', { method: 'POST', body: { ...body, session: session.current } });
      session.current = null;
      onPick(r.property);
      setQ('');
      setRes(null);
      setOpen(false);
      setMsg({ tone: 'ok', text: body.propertyId ? 'Address filled in from one we’ve used before.' : 'Address filled in. Check it, especially any flat number.' });
    } catch (e) {
      setMsg({ tone: 'bad', text: problem(e) });
    } finally {
      setBusy(false);
    }
  };

  const none = res && !res.noSuchPostcode && !res.saved.length && !res.suggestions.length;
  return (
    <div className="address-finder" ref={box}>
      <label className="fld">
        Find address
        <span className="finder-input">
          <Search size={18} aria-hidden="true" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setMsg(null); }}
            onFocus={() => res && setOpen(true)}
            placeholder="Type a postcode or the start of the address"
            autoComplete="off"
            autoFocus={autoFocus}
            disabled={busy}
            aria-expanded={open}
            aria-controls="address-finder-list"
          />
        </span>
      </label>
      {open && res && (
        <ul className="finder-list" id="address-finder-list" role="listbox">
          {res.saved.map((p) => (
            <li key={`s${p.id}`}><button type="button" onClick={() => pick({ propertyId: p.id })} disabled={busy}><MapPin size={14} aria-hidden="true" /> {p.text}<span className="tag">Used before</span></button></li>
          ))}
          {res.suggestions.map((s) => (
            <li key={s.id}><button type="button" onClick={() => pick({ id: s.id })} disabled={busy}>{s.text}</button></li>
          ))}
          {res.noSuchPostcode && <li className="warn-text small finder-empty">That postcode doesn&apos;t exist. Check it on the customer&apos;s paperwork.</li>}
          {none && <li className="muted small finder-empty">No addresses found. Check the postcode, or type the address in below.</li>}
          {res.provider === 'google' && res.suggestions.length > 0 && <li className="muted small finder-empty finder-credit">powered by Google</li>}
          {!res.connected && <li className="muted small finder-empty">Address lookup isn&apos;t set up; only addresses used before are listed.</li>}
        </ul>
      )}
      {msg && <p className={`small ${msg.tone === 'bad' ? 'warn-text' : 'muted'}`}>{msg.text}</p>}
      {linked && !msg && <p className="small muted">Linked to a looked-up address. You can still edit the lines below.</p>}
    </div>
  );
}
